import { useState, type CSSProperties } from 'react'
import { useImageStatus } from '@versports/ui'

/**
 * Iniciales del club para el placeholder: hasta 2 letras, sacadas de las
 * palabras "reales" del nombre (se ignoran conectores y siglas de tipo FC/CA
 * quedan como están si son la única palabra).
 */
export function clubInitials(name?: string | null): string {
  const words = (name ?? '')
    .replace(/[^\p{L}\p{N} ]+/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0 && !['de', 'del', 'la', 'el', 'los', 'las', 'y'].includes(w.toLowerCase()))
  const [first, second] = words
  if (!first) return '?'
  if (!second) return first.slice(0, 2).toUpperCase()
  return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase()
}

export type ClubMarkProps = {
  /** `logo_url` del club. `null` / `''` / URL caída → placeholder. */
  logoUrl?: string | null
  /** Nombre del club: alimenta las iniciales del placeholder. */
  name?: string | null
  /** Lado de la caja en px (siempre cuadrada). Default 32. */
  size?: number
  /** Radio del borde en px. Default 6. */
  radius?: number
  /** Color de marca para tintar el placeholder (ej. `primary_color`). */
  fallbackColor?: string | null
  className?: string
  style?: CSSProperties
}

/**
 * Logo de un club, con placeholder garantizado.
 *
 * Invariante (bug mg 2026-09-07, "sigo viendo imagen rota en transiciones"):
 * este componente **nunca** deja pintar el ícono de imagen rota del browser ni
 * el texto del `alt`. Por eso:
 *
 * - `logo_url` vacío o nulo → placeholder de iniciales, sin montar `<img>`.
 * - `logo_url` presente → se precarga fuera del árbol con `useImageStatus`
 *   (reintentos con backoff, cancelados al desmontar) y el `<img>` visible se
 *   monta recién cuando la imagen terminó de cargar. Mientras carga y si el
 *   fallo termina siendo definitivo se ve el mismo placeholder de iniciales,
 *   así que una request abortada por un cambio de ruta o un reload no alcanza
 *   a pintar nada roto. Una carga tardía exitosa reemplaza el placeholder.
 * - El `<img>` lleva `alt=""`: nunca hay texto alternativo a la vista. El
 *   nombre del club lo dice el texto que acompaña a la marca en cada call site.
 *
 * Es un componente presentacional puro: no lee el `AuthContext`. Los callers
 * le pasan `logoUrl` / `name` de donde los tengan (`user.branding_club`,
 * `overview.club`, la fila del admin, el `kitAccess`...).
 */
export function ClubMark({
  logoUrl,
  name,
  size = 32,
  radius = 6,
  fallbackColor,
  className = '',
  style,
}: ClubMarkProps) {
  const src = logoUrl?.trim() || null
  const status = useImageStatus(src)
  // Red de contención por si el `<img>` ya montado falla igual (revalidación
  // caída después de la precarga). Anclado al `src`: cambiar de club arranca
  // limpio.
  const [visibleFailed, setVisibleFailed] = useState<string | null>(null)

  const box: CSSProperties = {
    width: size,
    height: size,
    borderRadius: radius,
    flexShrink: 0,
    ...style,
  }

  if (!src || status !== 'loaded' || visibleFailed === src) {
    return (
      <span
        aria-hidden="true"
        className={`inline-flex items-center justify-center border border-outline bg-surface-elevated font-bold uppercase leading-none text-text-muted ${className}`}
        style={{
          ...box,
          fontSize: Math.max(9, Math.round(size * 0.4)),
          letterSpacing: '0.02em',
          ...(fallbackColor
            ? { backgroundColor: fallbackColor, color: '#fff', borderColor: fallbackColor }
            : null),
        }}
      >
        {clubInitials(name)}
      </span>
    )
  }

  return (
    <img
      src={src}
      alt=""
      className={`object-contain ${className}`}
      style={box}
      decoding="async"
      onError={() => setVisibleFailed(src)}
    />
  )
}
